import React from 'react';
import { motion } from 'framer-motion';
import type { InvoiceTemplate } from './templates/InvoiceTemplates';

interface InvoiceATSInfoProps {
  template: InvoiceTemplate;
  language: 'ar' | 'en';
  isVisible: boolean;
  onToggleVisibility: () => void;
}

const InvoiceATSInfo: React.FC<InvoiceATSInfoProps> = ({
  template,
  language,
  isVisible,
  onToggleVisibility
}) => {
  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800"
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-bold text-gray-800 dark:text-white">
          {language === 'ar' ? 'معلومات توافق الفاتورة' : 'Invoice Compatibility Info'}
        </h4>
        <button
          onClick={onToggleVisibility}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4">
        {/* درجة التوافق */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'ar' ? 'درجة التوافق:' : 'Compatibility Score:'}
          </span>
          <div className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded-full ${
              template.atsCompatibility.score >= 95 ? 'bg-green-500' :
              template.atsCompatibility.score >= 85 ? 'bg-blue-500' :
              template.atsCompatibility.score >= 75 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}></div>
            <span className={`text-lg font-bold ${
              template.atsCompatibility.score >= 95 ? 'text-green-600 dark:text-green-400' :
              template.atsCompatibility.score >= 85 ? 'text-blue-600 dark:text-blue-400' :
              template.atsCompatibility.score >= 75 ? 'text-yellow-600 dark:text-yellow-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {template.atsCompatibility.score}/100
            </span>
          </div>
        </div>

        {/* شريط التقدم */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${
              template.atsCompatibility.score >= 95 ? 'bg-green-500' :
              template.atsCompatibility.score >= 85 ? 'bg-blue-500' :
              template.atsCompatibility.score >= 75 ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            style={{ width: `${template.atsCompatibility.score}%` }}
          ></div>
        </div>

        {/* المميزات والتوصيات */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h5 className="font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {language === 'ar' ? 'المميزات المتوافقة' : 'Compatible Features'}
            </h5>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              {template.atsCompatibility.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h5 className="font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {language === 'ar' ? 'التوصيات' : 'Recommendations'}
            </h5>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              {template.atsCompatibility.recommendations.map((recommendation, index) => (
                <li key={index} className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default InvoiceATSInfo; 